/**
 * AES-256-CBC + HMAC-SHA256 加密策略
 *
 * 方案：
 * - AES-256-CBC 对称加密（随机 IV，防重放）
 * - HMAC-SHA256 签名（防篡改）
 * - 固定对称密钥（通过环境变量注入）
 *
 * 运行要求：
 * - 使用 Web Crypto API 原生实现
 *
 * 传输格式：
 * - 请求体：Base64(IV + 密文)
 * - 请求头：X-Encrypted, X-Timestamp, X-Signature
 */

import {
  decryptAESOptimized,
  encryptAESOptimized,
  generateSignatureOptimized,
  verifySignatureOptimized,
} from './crypto-provider-factory';
import type { AsyncEncryptionStrategy } from './types';

/** AES-CBC + HMAC 密钥配置 */
export interface AESCBCHMACKeys {
  /** 请求加密密钥（Hex 字符串，64 字符 = 256 位） */
  requestKeyHex: string;
  /** 响应解密密钥（Hex 字符串，64 字符 = 256 位） */
  responseKeyHex: string;
}

/**
 * AES-256-CBC + HMAC-SHA256 加密策略
 */
export class AESCBCHMACStrategy implements AsyncEncryptionStrategy {
  private readonly requestKeyHex: string;
  private readonly responseKeyHex: string;

  constructor(keys: AESCBCHMACKeys) {
    if (!keys.requestKeyHex || keys.requestKeyHex.length !== 64) {
      throw new Error(
        '[Crypto AES-CBC-HMAC] Invalid request key: expected 64 hex chars',
      );
    }
    if (!keys.responseKeyHex || keys.responseKeyHex.length !== 64) {
      throw new Error(
        '[Crypto AES-CBC-HMAC] Invalid response key: expected 64 hex chars',
      );
    }
    this.requestKeyHex = keys.requestKeyHex;
    this.responseKeyHex = keys.responseKeyHex;
  }

  async encrypt(data: unknown, _timestamp: number): Promise<string> {
    const plaintext = typeof data === 'string' ? data : JSON.stringify(data);
    return encryptAESOptimized(plaintext, this.requestKeyHex);
  }

  async decrypt(ciphertext: string, _timestamp: number): Promise<unknown> {
    const decrypted = await decryptAESOptimized(
      ciphertext,
      this.responseKeyHex,
    );
    try {
      return JSON.parse(decrypted);
    } catch {
      return decrypted;
    }
  }

  /** 生成请求签名 */
  async generateRequestSignature(
    encryptedData: string,
    timestamp: number,
  ): Promise<string> {
    const signContent = `${encryptedData}|${timestamp}`;
    return generateSignatureOptimized(signContent, this.requestKeyHex);
  }

  /** 验证响应签名 */
  async verifyResponseSignature(
    encryptedData: string,
    signature: string,
    timestamp: number,
  ): Promise<boolean> {
    const signContent = `${encryptedData}|${timestamp}`;
    return verifySignatureOptimized(
      signContent,
      signature,
      this.responseKeyHex,
    );
  }

  /** 生成响应签名（便于测试或对接模拟端） */
  async generateResponseSignature(
    encryptedData: string,
    timestamp: number,
  ): Promise<string> {
    const signContent = `${encryptedData}|${timestamp}`;
    return generateSignatureOptimized(signContent, this.responseKeyHex);
  }
}
